package fr.frux.feature.site.detail

import fr.frux.core.common.error.asAppError
import fr.frux.core.common.error.isExpectedFailure
import fr.frux.core.common.logging.Logger
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.SiteAccessRule
import fr.frux.shared.domain.repository.SiteRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class SiteOwnershipUi(
    val busy: Boolean = false,
    val unclaimPrompt: Boolean = false,
)

/**
 * « C'est chez moi », règle d'accès et retrait du propriétaire. Le lieu rendu par le serveur arrive à la
 * fiche par `SiteRepository.changes`, ce contrôleur ne touche donc jamais à son état.
 */
class SiteOwnershipController(
    private val siteRepository: SiteRepository,
    private val scope: CoroutineScope,
    private val isSignedIn: () -> Boolean,
    private val onLoginRequired: () -> Unit,
    private val onMessage: (SiteDetailMessage) -> Unit,
) {
    private val _state = MutableStateFlow(SiteOwnershipUi())
    val state: StateFlow<SiteOwnershipUi> = _state.asStateFlow()

    fun claim(siteId: Long) = run(SiteDetailMessage.Claimed) { siteRepository.claim(siteId) }

    fun setAccessRule(
        siteId: Long,
        rule: SiteAccessRule,
    ) = run(SiteDetailMessage.AccessRuleChanged) { siteRepository.setAccessRule(siteId, rule) }

    fun askUnclaim() = _state.update { it.copy(unclaimPrompt = true) }

    fun cancelUnclaim() = _state.update { it.copy(unclaimPrompt = false) }

    fun confirmUnclaim(siteId: Long) {
        cancelUnclaim()
        run(SiteDetailMessage.Unclaimed) { siteRepository.unclaim(siteId) }
    }

    /** Un seul geste en vol : le verrou est posé avant le lancement, un second appui ne part pas. */
    private fun run(
        done: SiteDetailMessage,
        call: suspend () -> Result<Site>,
    ) {
        if (!isSignedIn()) {
            onLoginRequired()
            return
        }
        if (_state.value.busy) return
        _state.update { it.copy(busy = true) }
        scope.launch {
            call()
                .onSuccess { onMessage(done) }
                .onFailure { error ->
                    if (error.isExpectedFailure) {
                        Logger.i(TAG, "Geste de propriétaire refusé : ${error.message}")
                    } else {
                        Logger.e(TAG, "Échec d'un geste de propriétaire", error)
                    }
                    onMessage(SiteDetailMessage.Failed(error.asAppError()))
                }
            _state.update { it.copy(busy = false) }
        }
    }

    private companion object {
        const val TAG = "SiteOwnershipController"
    }
}
